import { createParser, createSerializer, parseAsString } from 'nuqs'
import { createLoader } from 'nuqs/server'
import { parseColor } from 'react-aria-components'
import {
	DEFAULT_COLOR,
	DEFAULT_COLOR_SPACE,
	DEFAULT_DARK_MODE,
	DEFAULT_STROKE_WIDTH,
} from './misc'

const parseAsColor = createParser({
	parse(queryValue) {
		if (!queryValue) return parseColor(DEFAULT_COLOR)
		try {
			return parseColor(queryValue)
		} catch {
			return parseColor(DEFAULT_COLOR)
		}
	},
	serialize(value) {
		return value.toString('hsb')
	},
})

const parseAsColorSpace = createParser({
	parse(queryValue) {
		const validSpaces = ['hex', 'rgb', 'hsl', 'hsb'] as const
		if (!queryValue || !validSpaces.includes(queryValue as any)) {
			return DEFAULT_COLOR_SPACE
		}
		return queryValue as 'hex' | 'rgb' | 'hsl' | 'hsb'
	},
	serialize(value) {
		return value
	},
})

const parseAsDarkMode = createParser({
	parse(queryValue) {
		return queryValue === 'true' ? 'true' : DEFAULT_DARK_MODE
	},
	serialize(value) {
		return value
	},
})

const parseAsStrokeWidth = createParser({
	parse(queryValue) {
		if (!queryValue) return DEFAULT_STROKE_WIDTH
		const value = parseFloat(queryValue)
		if (isNaN(value) || value < 0.25 || value > 3) {
			return DEFAULT_STROKE_WIDTH
		}
		return value
	},
	serialize(value) {
		return value.toString()
	},
})

export type IconFormat =
	| 'notion'
	| 'download-svg'
	| 'copy-svg'
	| 'copy-react'
	| 'copy-vue'
	| 'copy-svelte'
	| 'copy-solid'
export const DEFAULT_FORMAT: IconFormat = 'notion'

export type SearchType = 'smart' | 'exact'
export const DEFAULT_SEARCH_TYPE: SearchType = 'smart'

export type Grouping = 'grouped' | 'ungrouped'
export const DEFAULT_GROUPING: Grouping = 'ungrouped'

export type IconName = 'true' | 'false'
export const DEFAULT_ICON_NAME: IconName = 'true'

export function sanitizeQuery(query: string): string {
	return query.replace(/[^\x00-\x7F]/g, '')
}

const parseAsAsciiQuery = createParser({
	parse(queryValue) {
		if (!queryValue) return ''
		return sanitizeQuery(queryValue)
	},
	serialize(value) {
		return value
	},
})

const parseAsSearchType = createParser({
	parse(queryValue) {
		const valid = ['smart', 'exact'] as const
		if (!queryValue || !valid.includes(queryValue as any)) {
			return DEFAULT_SEARCH_TYPE
		}
		return queryValue as SearchType
	},
	serialize(value) {
		return value
	},
})

const parseAsGrouping = createParser({
	parse(queryValue) {
		const valid = ['grouped', 'ungrouped'] as const
		if (!queryValue || !valid.includes(queryValue as any)) {
			return DEFAULT_GROUPING
		}
		return queryValue as Grouping
	},
	serialize(value) {
		return value
	},
})

const parseAsIconName = createParser({
	parse(queryValue) {
		return queryValue === 'false' ? 'false' : DEFAULT_ICON_NAME
	},
	serialize(value) {
		return value
	},
})

// Export raw parser for dynamic default usage in hooks
export const parseAsFormat = createParser({
	parse(queryValue) {
		const validFormats = [
			'notion',
			'download-svg',
			'copy-svg',
			'copy-react',
			'copy-vue',
			'copy-svelte',
			'copy-solid',
		] as const
		if (!queryValue || !validFormats.includes(queryValue as IconFormat)) {
			return null // determine default based on domain config at runtime
		}
		return queryValue as IconFormat
	},
	serialize(value) {
		return value
	},
})

// Parsers with defaults only - options are now set globally in NuqsAdapter
export const searchParamsParsers = {
	query: parseAsAsciiQuery.withDefault(''),
	color: parseAsColor.withDefault(parseColor(DEFAULT_COLOR)),
	colorSpace: parseAsColorSpace.withDefault(DEFAULT_COLOR_SPACE),
	darkMode: parseAsDarkMode.withDefault(DEFAULT_DARK_MODE),
	strokeWidth: parseAsStrokeWidth.withDefault(DEFAULT_STROKE_WIDTH),
	path: parseAsString.withDefault(''),
	redirectTo: parseAsString.withDefault(''),
	searchType: parseAsSearchType.withDefault(DEFAULT_SEARCH_TYPE),
	grouping: parseAsGrouping.withDefault(DEFAULT_GROUPING),
	iconName: parseAsIconName.withDefault(DEFAULT_ICON_NAME),
	format: parseAsFormat.withDefault(DEFAULT_FORMAT),
}

// Server-side loader for parsing search params
export const loadSearchParams = createLoader(searchParamsParsers)

// Serializer for building URLs with search params
export const serializeSearchParams = createSerializer(searchParamsParsers)
